import React from 'react';
import { useCallback } from 'react';
import {
  ParticipantField,
  ParticipantFieldType,
  Participant,
  ParticipantTag,
  Income,
  usePanel,
  ExtendedParticipantField,
  ExtendedParticipant,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Chip } from '@app/core';
import { getIncomeText } from './incomes';

export const useGetParticipantDisplayValue = (panelId?: string) => {
  const { t } = useTranslation('common');

  const { data: panel } = usePanel(panelId);
  const tags = panel?.tags;

  return useCallback(
    (
      participant: Partial<Participant | ExtendedParticipant>,
      field: ParticipantField | ExtendedParticipantField,
    ): React.ReactNode => {
      let rawValue = participant[field.name as keyof Participant];
      if (!rawValue || (Array.isArray(rawValue) && !rawValue.length)) {
        if (field.table?.backupKey) {
          rawValue = participant[field.table.backupKey as keyof Participant];
        }

        if (!rawValue || (Array.isArray(rawValue) && !rawValue.length)) {
          return '';
        }
      }

      if (field.choices) {
        const { choices } = field;
        const arrayValue = (
          Array.isArray(rawValue) ? rawValue : [rawValue]
        ) as string[];

        const results = arrayValue.map((val: unknown) => {
          const strVal = String(val);
          const found = choices.find(({ value }) => String(value) === strVal);
          if (!found) {
            return val;
          }
          return t(found.label);
        });

        return results.join(', ');
      }

      switch (field.type) {
        case ParticipantFieldType.DATE:
          if (field.displayAsAge) {
            return t('participants.ageValue', {
              count: moment().diff(String(rawValue), 'years'),
            });
          }
          return moment(String(rawValue)).format('LL');

        case ParticipantFieldType.TAGS:
          let value =
            typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
          if (!Array.isArray(value)) {
            return '';
          }
          return (value as Array<ParticipantTag | string>).map((tagId) => {
            let tag: ParticipantTag | undefined;
            if (typeof tagId === 'string') {
              tag = tags?.find(({ id }) => id === tagId);
            } else {
              tag = tagId;
            }
            if (!tag) {
              return '';
            }
            return (
              <Chip customColor={tag.color} key={tag.id}>
                {tag.name}
              </Chip>
            );
          });
        case ParticipantFieldType.INCOME:
          return getIncomeText(rawValue as Income, participant.countryKey);

        default:
          return rawValue as React.ReactNode;
      }
    },
    [t, tags],
  );
};

export const useGetParticipantDisplayText = () => {
  const { t } = useTranslation('common');

  return useCallback(
    (
      participant: Partial<Participant | ExtendedParticipant>,
      field: ParticipantField | ExtendedParticipantField,
    ): string | undefined => {
      let rawValue = participant[field.name as keyof Participant];
      if (!rawValue || (Array.isArray(rawValue) && !rawValue.length)) {
        return undefined;
      }

      if (field.choices) {
        const { choices } = field;
        const arrayValue = (
          Array.isArray(rawValue) ? rawValue : [rawValue]
        ) as string[];

        const results = arrayValue.map((val: unknown) => {
          const strVal = String(val);
          const found = choices.find(({ value }) => String(value) === strVal);
          if (!found) {
            return val;
          }
          return t(found.label);
        });

        return results.join(', ');
      }

      switch (field.type) {
        case ParticipantFieldType.DATE:
          if (field.displayAsAge) {
            return t('participants.ageValue', {
              count: moment().diff(String(rawValue), 'years'),
            });
          }
          return moment(String(rawValue)).format('LL');

        case ParticipantFieldType.TAGS:
          if (typeof rawValue === 'string') {
            return rawValue;
          }
          if (Array.isArray(rawValue)) {
            return rawValue
              .map((tag) => (tag as ParticipantTag).name)
              .join(', ');
          }
          return undefined;
        case ParticipantFieldType.INCOME:
          return getIncomeText(rawValue as Income, participant.countryKey);

        default:
          return String(rawValue);
      }
    },
    [t],
  );
};
